import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import { Analytics, LoaderComp } from 'ui-kit';
import { useSubscription } from '../hooks';
const AnalyticsPageNew = ({ themeName }) => {
    const lastThemeName = useRef(themeName);
    const { data: statsData } = useSubscription('stats');
    const { data: protocolData } = useSubscription('protocol');
    // Format a "YYYY-MM-DD" date string (or numeric Unix ms) into "DD MMM"
    const formatTimestamp = (timestamp) => {
        const numericTs = Number(timestamp);
        const date = isNaN(numericTs) ? new Date(timestamp) : new Date(numericTs * 1000);
        if (isNaN(date.getTime()))
            return timestamp;
        return `${date.getUTCDate()} ${date.toLocaleString('default', { month: 'short', timeZone: 'UTC' })}`;
    };
    const graphThemes = useMemo(() => ({
        tooltipTextColor: themeName === 'dark' ? '#FFFFFF' : '#293036',
        tooltipBgColor: themeName === 'dark' ? '#202226' : '#FFFFFF',
        tooltipBorderColor: themeName === 'default' ? '#DEE4EF' : '#202226',
    }), [themeName]);
    const analyticsData = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        if (!statsData)
            return null;
        const distributionMap = (_b = (_a = statsData.treasury) === null || _a === void 0 ? void 0 : _a.treasury_holdings_distribution) !== null && _b !== void 0 ? _b : {};
        return {
            latestTVL: protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl.total_tvl,
            annualisedRevenue: statsData.annualized_revenue,
            dailyRevenue: (_d = (_c = statsData.daily_revenue) === null || _c === void 0 ? void 0 : _c.total) !== null && _d !== void 0 ? _d : '0',
            totalAlphaStakers: Number((_e = statsData.total_alpha_stakers) !== null && _e !== void 0 ? _e : 0),
            treasuryTotalHoldingsUsd: (_f = statsData.treasury) === null || _f === void 0 ? void 0 : _f.treasury_total_holdings_usd,
            treasuryAlphaHolding: (_g = statsData.treasury) === null || _g === void 0 ? void 0 : _g.treasury_alpha_holding,
            treasuryStsuiHolding: (_h = statsData.treasury) === null || _h === void 0 ? void 0 : _h.treasury_stsui_holding,
            circulatingAlphaSupply: (_j = statsData.alpha_breakdown) === null || _j === void 0 ? void 0 : _j.circulating_supply,
            maxAlphaSupply: (_k = statsData.alpha_breakdown) === null || _k === void 0 ? void 0 : _k.max_supply,
            treasurySharePerAlpha: (_l = statsData.alpha_breakdown) === null || _l === void 0 ? void 0 : _l.treasury_share_per_alpha,
            airdropApr: (_m = statsData.alpha_breakdown) === null || _m === void 0 ? void 0 : _m.alpha_vault_apr,
            historicalTVL: {
                time: ((_o = statsData.historical_tvl_graph) !== null && _o !== void 0 ? _o : []).map((p) => formatTimestamp(p.timestamp)),
                label: 'Total Value Locked',
                TVL: ((_p = statsData.historical_tvl_graph) !== null && _p !== void 0 ? _p : []).map((p) => parseFloat(p.tvl)),
            },
            alphaHistoricalMarketCap: {
                time: ((_r = (_q = statsData.alpha_breakdown) === null || _q === void 0 ? void 0 : _q.alpha_historical_market_cap_graph) !== null && _r !== void 0 ? _r : []).map((p) => formatTimestamp(p.timestamp)),
                label: 'ALPHA Market Cap',
                TVL: ((_t = (_s = statsData.alpha_breakdown) === null || _s === void 0 ? void 0 : _s.alpha_historical_market_cap_graph) !== null && _t !== void 0 ? _t : []).map((p) => parseFloat(p.market_cap)),
            },
            treasuryHoldingsDistribution: [
                {
                    ALPHA: (_u = distributionMap['ALPHA']) !== null && _u !== void 0 ? _u : '0',
                    stSUI: (_w = (_v = distributionMap['stSUI']) !== null && _v !== void 0 ? _v : distributionMap['STSUI']) !== null && _w !== void 0 ? _w : '0',
                },
            ],
            graphThemes,
        };
    }, [statsData, graphThemes, protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl.total_tvl]);
    // Update graphThemes immediately when theme changes without re-deriving everything
    useEffect(() => {
        lastThemeName.current = themeName;
    }, [themeName]);
    // Full-page loader only until we have enough stats to render. Do not couple to
    // useSubscription isLoading — it can pulse during WS reconnect even when localStorage cache still has data.
    if (!analyticsData) {
        return (_jsx("div", { className: "flex items-center justify-center h-screen mt-[-4vw] theme-bg-alphafi-primary", children: _jsx(LoaderComp, { dimensions: "w-[9vw] md:w-[3vw] h-[9vw] md:h-[3vw]", borderWidth: "border-[0.5vw]" }) }));
    }
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "mt-[-1px]", children: [_jsx("div", { className: "text-xl md:text-2xl xl:text-3xl font-InterBold theme-bg-alphafi-primary theme-text-alphafi-primary py-[36px] xl:py-[64px] text-center", children: "Analytics" }), _jsx("div", { className: "mt-[-1px]", children: _jsx(Analytics, Object.assign({}, analyticsData, { graphThemes: analyticsData.graphThemes })) })] }) }));
};
export default AnalyticsPageNew;
